import { ColorMenu } from '@model-framework/color/ColorMenu';
import type { LinkLineStyle, LinkMarkStyle } from '@model-framework/link';
import { LinkColor, LinkLineStyleMenuView, LinkMarkStyleMenuView, ReverseLinkButton } from '@model-framework/link';
import { StickyNodeGroupingMenuButton } from '@model-framework/menu/StickyNodeGroupingMenuButton';
import { FontSize, FontSizeMenu } from '@model-framework/text';
import { ThemeColor } from '@view-model/models/common/color';
import type { TextAlign } from '@view-model/models/common/text-align';
import { TextAlignMenu } from '@view-model/models/common/text-align';
import { LinkStyle } from '@view-model/models/sticky/StickyLink';
import { MenuSize, MultiSelectionMenuId } from '@view-model/models/sticky/ui';
// /StickyZoneViewや/StickyNodeView から読み込むと実行時エラーが出てしまうので、一段下の/domainから読み込む
import { NodeFontSize } from '@view-model/models/sticky/StickyNodeView/domain';
import { StickyZoneFontSize, StickyZoneShape } from '@view-model/models/sticky/StickyZoneView/domain';
import { ZoneShapeMenu } from '@view-model/models/sticky/StickyZoneView/components/StickyZoneMenu/ZoneShapeMenu';
import { RemoveMultiSelectedLinksButton } from './RemoveMultiSelectedLinksButton';
import type { AlignType } from './components';
import { AlignMenu } from './components';
import { FontStrikethroughMenuButton } from '@model-framework/menu/FontStrikethroughMenuButton';
import { useTranslation } from 'react-i18next';
type Props = {
  menuId: MultiSelectionMenuId;
  isOpen: boolean;
  currentFontSize?: FontSize;
  currentThemeColor?: ThemeColor;
  currentTextAlign?: TextAlign;
  currentLinkLineStyle?: LinkLineStyle;
  currentLinkMarkStyle?: LinkMarkStyle;
  currentLinkColor?: LinkColor;
  currentZoneShape?: StickyZoneShape;
  onMenuOpen(): void;
  onMenuClose(): void;
  onFontSizeSelected(fontSize: FontSize): void;
  onToggleFontStrikethrough(): void;
  onThemeColorSelected(themeColor: ThemeColor): void;
  onTextAlignSelected(textAlign: TextAlign): void;
  onZoneShapeSelected(shape: StickyZoneShape): void;
  onGroupSelectedNodes(): void;
  onLinkLineStyleSelected(lineStyle: LinkLineStyle): void;
  onLinkMarkStyleSelected(markStyle: LinkMarkStyle): void;
  onReverseLinks(): void;
  onRemoveLinks(): void;
  onLinkColorSelected(color: LinkColor): void;
  onAlignSelected(alignType: AlignType, interval: number): void;
  isZoneOnly: boolean;
};

const fontSizeMenuItems = (isZoneOnly: boolean): FontSize[] => {
  if (isZoneOnly) {
    return FontSize.values().filter((fontSize) => StickyZoneFontSize.include(fontSize));
  }

  return FontSize.values().filter((fontSize) => NodeFontSize.include(fontSize) && StickyZoneFontSize.include(fontSize));
};

export const MultiSelectionMenuItem: React.FC<Props> = ({
  menuId,
  isOpen,
  currentFontSize,
  currentThemeColor,
  currentTextAlign,
  currentLinkLineStyle,
  currentLinkMarkStyle,
  currentLinkColor,
  currentZoneShape,
  onMenuOpen,
  onMenuClose,
  onFontSizeSelected,
  onToggleFontStrikethrough,
  onThemeColorSelected,
  onTextAlignSelected,
  onZoneShapeSelected,
  onGroupSelectedNodes,
  onLinkLineStyleSelected,
  onLinkMarkStyleSelected,
  onReverseLinks,
  onRemoveLinks,
  onLinkColorSelected,
  onAlignSelected,
  isZoneOnly,
}: Props) => {
  const { t } = useTranslation();
  return (
    <>
      {menuId === MultiSelectionMenuId.FontSize && (
        <FontSizeMenu
          currentFontSize={currentFontSize}
          menuSize={MenuSize}
          items={fontSizeMenuItems(isZoneOnly)}
          isOpen={isOpen}
          onMenuClicked={isOpen ? onMenuClose : onMenuOpen}
          onSelected={onFontSizeSelected}
          text={t('canvas.menu.textSize')}
        />
      )}

      {menuId === MultiSelectionMenuId.ThemeColor && (
        <ColorMenu
          currentColorName={currentThemeColor}
          menuSize={MenuSize}
          items={ThemeColor.menuItems()}
          numColumns={4}
          isOpen={isOpen}
          onMenuClicked={isOpen ? onMenuClose : onMenuOpen}
          onSelected={onThemeColorSelected}
          text={t('canvas.menu.color')}
        />
      )}

      {menuId === MultiSelectionMenuId.TextAlign && (
        <TextAlignMenu
          currentTextAlign={currentTextAlign}
          menuSize={MenuSize}
          isOpen={isOpen}
          onMenuClicked={isOpen ? onMenuClose : onMenuOpen}
          onSelected={onTextAlignSelected}
          text={t('canvas.menu.textAlign')}
        />
      )}

      {menuId === MultiSelectionMenuId.ZoneShape && (
        <ZoneShapeMenu
          currentShape={currentZoneShape}
          menuSize={MenuSize}
          items={StickyZoneShape.values()}
          isOpen={isOpen}
          onMenuClicked={isOpen ? onMenuClose : onMenuOpen}
          onSelected={onZoneShapeSelected}
          text={t('canvas.menu.zone.shape')}
        />
      )}

      {menuId === MultiSelectionMenuId.FontStrikethrough && (
        <FontStrikethroughMenuButton
          menuSize={MenuSize}
          onClick={onToggleFontStrikethrough}
          text={t('canvas.menu.strikethrough')}
        />
      )}

      {menuId === MultiSelectionMenuId.GroupStickyElements && (
        <StickyNodeGroupingMenuButton
          menuSize={MenuSize}
          onClick={() => {
            // 他のメニューが開いていた場合閉じる
            onMenuClose();
            onGroupSelectedNodes();
          }}
          text={t('canvas.menu.group')}
        />
      )}

      {menuId === MultiSelectionMenuId.RemoveMultiSelectedLinks && (
        <RemoveMultiSelectedLinksButton
          menuSize={MenuSize}
          onClick={onRemoveLinks}
          text={t('canvas.menu.multiSelection.removeLinks')}
        />
      )}

      {menuId === MultiSelectionMenuId.LinkLineStyle && (
        <LinkLineStyleMenuView
          menuSize={MenuSize}
          currentLineStyle={currentLinkLineStyle}
          items={LinkStyle.lineStyles}
          isOpen={isOpen}
          onMenuClicked={isOpen ? onMenuClose : onMenuOpen}
          onSelected={onLinkLineStyleSelected}
          text={t('canvas.menu.lineStyle')}
        />
      )}

      {menuId === MultiSelectionMenuId.LinkMarkStyle && (
        <LinkMarkStyleMenuView
          menuSize={MenuSize}
          currentMarkStyle={currentLinkMarkStyle}
          items={LinkStyle.markStyles}
          isOpen={isOpen}
          onMenuClicked={isOpen ? onMenuClose : onMenuOpen}
          onSelected={onLinkMarkStyleSelected}
          text={t('canvas.menu.linkType')}
        />
      )}

      {menuId === MultiSelectionMenuId.ReverseLink && (
        <ReverseLinkButton menuSize={MenuSize} onClick={onReverseLinks} text={t('canvas.menu.reverseLink')} />
      )}

      {menuId === MultiSelectionMenuId.LinkColor && (
        <ColorMenu
          menuSize={MenuSize}
          currentColorName={currentLinkColor}
          items={LinkColor.menuItems()}
          isOpen={isOpen}
          onMenuClicked={isOpen ? onMenuClose : onMenuOpen}
          onSelected={onLinkColorSelected}
          text={t('canvas.menu.color')}
        />
      )}

      {menuId === MultiSelectionMenuId.HorizontalAlignMenu && (
        <AlignMenu
          menuSize={MenuSize}
          alignType="horizontal"
          isOpen={isOpen}
          onMenuClicked={isOpen ? onMenuClose : onMenuOpen}
          onSelected={onAlignSelected}
          text={t('canvas.menu.multiSelection.horizontalAlign')}
        />
      )}

      {menuId === MultiSelectionMenuId.VerticalAlignMenu && (
        <AlignMenu
          menuSize={MenuSize}
          alignType="vertical"
          isOpen={isOpen}
          onMenuClicked={isOpen ? onMenuClose : onMenuOpen}
          onSelected={onAlignSelected}
          text={t('canvas.menu.multiSelection.verticalAlign')}
        />
      )}
    </>
  );
};
